import React from 'react';
import { Link } from 'react-router-dom';

export type LinkButtonVariant = 'primary' | 'secondary';

/**
 * An anchor that looks like a design-system Button.
 *
 * Navigation belongs in a link — wrapping a <Button> in a <Link> would nest one
 * interactive element inside another, which breaks keyboard and screen-reader
 * behaviour. Mirrors `Button`'s small size so the two sit together in a table row.
 */
const baseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  padding: '7px 13px',
  fontSize: 13,
  fontFamily: 'var(--font-sans)',
  lineHeight: 1,
  borderRadius: 'var(--radius-md)',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  cursor: 'pointer',
};

const VARIANTS: Record<LinkButtonVariant, React.CSSProperties> = {
  primary: { background: 'var(--lime)', color: 'var(--lime-fg)', border: '1px solid transparent', fontWeight: 700 },
  secondary: {
    background: 'var(--surface-3)',
    color: 'var(--text-strong)',
    border: '1px solid var(--border)',
    fontWeight: 600,
  },
};

export interface LinkButtonProps {
  /** Internal route. Pass `href` instead for an external destination. */
  to?: string;
  /** External URL — opened in a new tab. */
  href?: string;
  variant?: LinkButtonVariant;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
  /** Router state for internal links (e.g. the origin a detail page returns to). */
  state?: unknown;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const LinkButton: React.FC<LinkButtonProps> = ({
  to,
  href,
  variant = 'secondary',
  icon,
  iconRight,
  onClick,
  state,
  children,
  style,
}) => {
  const composed = { ...baseStyle, ...VARIANTS[variant], ...style };
  const content = (
    <>
      {icon}
      {children}
      {iconRight}
    </>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} style={composed}>
        {content}
      </a>
    );
  }

  return (
    <Link to={to || '#'} state={state} onClick={onClick} style={composed}>
      {content}
    </Link>
  );
};

export default LinkButton;
